import { useCallback, useMemo, useState } from 'react';
import type { SyntheticEvent } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useMediaQuery, useTheme } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import {
  buildFileCategoryTree,
  findFileCategoryAncestorIds,
  flattenFileCategoryTree,
} from './utils/build-file-category-tree.util';
import {
  fileCategoryAccessOf,
  isFileCategoryLocked,
} from '../../utils/file-category-access.util';
import type { FileCategoryAccesses } from '../../types/file-category-access.type';

import { renderFileCategoryTreeItems } from './utils/render-file-category-tree.util';
import type { FileCategory } from './utils/render-file-category-tree.util';

interface FileCategoryListProps {
  readonly accesses?: FileCategoryAccesses;
  readonly categories: readonly FileCategory[];
  readonly fileCountsByCategoryId?: ReadonlyMap<Id, number>;
  readonly selectedId: Id | null;
  readonly isLoading: boolean;
  readonly onSelect: (id: Id) => void;
}

export const FileCategoryList = ({
  accesses,
  categories,
  fileCountsByCategoryId,
  selectedId,
  isLoading,
  onSelect,
}: Readonly<FileCategoryListProps>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tree = useMemo(() => buildFileCategoryTree(categories), [categories]);

  const [manuallyExpandedIds, setManuallyExpandedIds] =
    useState<ReadonlySet<Id> | null>(null);

  const selectionAncestorIds = useMemo(
    () =>
      selectedId
        ? findFileCategoryAncestorIds(categories, selectedId)
        : EMPTY_ARRAY,
    [categories, selectedId],
  );

  const expandedIds = useMemo(
    () =>
      manuallyExpandedIds === null
        ? new Set(categories.map((category) => category._id))
        : new Set([...manuallyExpandedIds, ...selectionAncestorIds]),
    [categories, manuallyExpandedIds, selectionAncestorIds],
  );

  const handleExpandedItemsChange = useCallback(
    (_event: SyntheticEvent | null, itemIds: string[]) => {
      setManuallyExpandedIds(new Set(itemIds as Id[]));
    },
    [],
  );

  const handleSelectedItemsChange = useCallback(
    (_event: SyntheticEvent | null, itemId: string | null) => {
      if (itemId) {
        onSelect(itemId as Id);
      }
    },
    [onSelect],
  );

  const flattenedCategories = useMemo(
    () => flattenFileCategoryTree(tree),
    [tree],
  );

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, newValue: Id) => {
      onSelect(newValue);
    },
    [onSelect],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Tabs
        allowScrollButtonsMobile
        aria-label="Catégories"
        onChange={handleTabChange}
        scrollButtons
        value={selectedId ?? false}
        variant="scrollable"
      >
        {flattenedCategories.map(({ category, breadcrumbLabel }) => {
          const fileCount = fileCountsByCategoryId?.get(category._id);

          return (
            <Tab
              disabled={isFileCategoryLocked(
                fileCategoryAccessOf(accesses, category._id),
              )}
              key={category._id}
              label={
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                  {breadcrumbLabel}
                  {fileCount ? (
                    <Typography color="text.secondary" variant="caption">
                      ({fileCount})
                    </Typography>
                  ) : null}
                </Box>
              }
              value={category._id}
            />
          );
        })}
      </Tabs>
    );
  }

  return (
    <>
      <Typography sx={{ pb: 1, pt: 2, px: 2 }} variant="overline">
        Catégories
      </Typography>
      <SimpleTreeView
        expandedItems={[...expandedIds]}
        onExpandedItemsChange={handleExpandedItemsChange}
        onSelectedItemsChange={handleSelectedItemsChange}
        selectedItems={selectedId}
        sx={{ px: 1 }}
      >
        {renderFileCategoryTreeItems(tree, fileCountsByCategoryId, accesses)}
      </SimpleTreeView>
    </>
  );
};
